import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { StrongAuthPanel } from "@/components/aion/StrongAuthPanel";
import { listActionRecords, listPendingActionReconciliations, reconcileUnknownAction } from "@/services/actionReconciliation";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const actionTimestamp = (action) => action.completed_at || action.created_at || "";

export const ActionReconciliationPanel = ({ token }) => {
  const [actions, setActions] = useState([]);
  const [status, setStatus] = useState("LOADING");
  const [error, setError] = useState("");
  const [busyActionId, setBusyActionId] = useState("");

  const loadActions = useCallback(async () => {
    setStatus("LOADING");
    setError("");
    try {
      const [reconciliationResult, actionRecords] = await Promise.all([
        listPendingActionReconciliations(token, 20),
        listActionRecords(token, 50),
      ]);
      const supportedActions = (Array.isArray(reconciliationResult?.actions) ? reconciliationResult.actions : []).map((item) => ({
        ...item,
        reconciliation_supported: true,
      }));
      const supportedIds = new Set(supportedActions.map((item) => item.action_id).filter(Boolean));
      const unsupportedActions = (Array.isArray(actionRecords) ? actionRecords : [])
        .filter((item) => item?.status === "UNKNOWN_RESULT" && !supportedIds.has(item.action_id))
        .map((item) => ({
          action_id: item.action_id,
          status: item.status,
          capability: item.capability,
          target_type: item.target_type,
          target_id: item.target_id,
          created_at: item.created_at,
          completed_at: item.completed_at,
          message: item.verification_result?.message || item.execution_result?.message || "Der Endzustand konnte nicht sicher bestätigt werden.",
          retry_policy: item.execution_result?.retry_policy,
          blind_retry_allowed: false,
          reconciliation_required: true,
          reconciliation_supported: false,
          provider: item.execution_result?.provider_reference?.provider_id || item.verification_result?.provider_reference?.provider_id || null,
          provider_event_id: item.execution_result?.provider_reference?.event_id || item.verification_result?.provider_reference?.event_id || null,
        }));
      const nextActions = [...supportedActions, ...unsupportedActions]
        .sort((left, right) => actionTimestamp(right).localeCompare(actionTimestamp(left)));
      setActions(nextActions);
      setStatus(nextActions.length ? "READY" : "EMPTY");
    } catch (loadError) {
      setError(String(loadError?.message || "Unklare Aktionen konnten nicht geladen werden."));
      setStatus("ERROR");
    }
  }, [token]);

  useEffect(() => {
    void loadActions();
  }, [loadActions]);

  const reconcile = async (actionId) => {
    setBusyActionId(actionId);
    setError("");
    try {
      await reconcileUnknownAction(actionId, token);
      await loadActions();
    } catch (reconcileError) {
      setError(String(reconcileError?.message || "Aktionsstatus konnte nicht geprüft werden."));
    } finally {
      setBusyActionId("");
    }
  };

  return (
    <>
      <StrongAuthPanel token={token} />
      <AionSectionCard
        title="Unklare Aktionen"
        eyebrow="RECONCILIATION"
        subtitle="AION zeigt alle eigenen UNKNOWN_RESULT-ActionRecords. Nur vorhandene sichere Read-only-Strategien dürfen eine Prüfung starten; niemals wird automatisch erneut ausgeführt."
        action={(
          <Button variant="outline" onClick={() => void loadActions()} disabled={status === "LOADING"} data-testid="action-reconciliation-refresh-button">
            <RefreshCw aria-hidden="true" /> {status === "LOADING" ? "Lädt …" : "Status neu laden"}
          </Button>
        )}
        testId="action-reconciliation-card"
      >
        <div className="aion-note" data-testid="action-reconciliation-safety-note">
          <ShieldCheck aria-hidden="true" className="aion-list-icon" /> Blind Retry: NEIN. „Ergebnis prüfen“ erscheint nur, wenn AION für diesen Aktionstyp bereits eine sichere Read-only-Reconciliation besitzt.
        </div>

        {error ? <div className="aion-error-box" data-testid="action-reconciliation-error">{error}</div> : null}

        {status === "ERROR" && !actions.length ? (
          <AionEmptyState
            icon={AlertTriangle}
            title="Reconciliation konnte nicht geladen werden."
            description="Die Aktion wird dadurch nicht erneut ausgeführt. Lade nur den Status später erneut."
            testId="action-reconciliation-load-error"
          />
        ) : null}

        {status === "EMPTY" ? (
          <AionEmptyState
            icon={ShieldCheck}
            title="Keine unklaren Aktionen."
            description="Aktuell wartet kein eigener ActionRecord auf eine sichere Ergebnisprüfung."
            testId="action-reconciliation-empty"
          />
        ) : null}

        {actions.length ? (
          <div className="aion-list" data-testid="action-reconciliation-list">
            {actions.map((action, index) => {
              const lastCheck = action.last_reconciliation;
              const eventSuffix = action.provider_event_id ? String(action.provider_event_id).slice(-10) : "—";
              return (
                <div className="aion-list-row" key={action.action_id || index} data-testid={`action-reconciliation-item-${index}`}>
                  <div>
                    <strong><AlertTriangle className="aion-list-icon" aria-hidden="true" /> {action.capability || "Aktion"} · UNKNOWN_RESULT</strong>
                    <small>{action.message || "Der Endzustand konnte nicht sicher bestätigt werden."}</small>
                    <small>Action {String(action.action_id || "").slice(-10)} · Typ {action.target_type || "—"}{action.provider ? ` · Provider ${action.provider}` : ""}{action.provider_event_id ? ` · Event …${eventSuffix}` : ""}</small>
                    <small>Abgeschlossen: {formatTimestamp(action.completed_at || action.created_at)}</small>
                    {lastCheck ? <small>Letzte Prüfung: {lastCheck.code} · {formatTimestamp(lastCheck.checked_at)} · {lastCheck.message}</small> : null}
                    <small>Blind Retry: NEIN · Wiederholen ist in dieser Oberfläche nicht verfügbar.</small>
                    {!action.reconciliation_supported ? <small>Für diesen Aktionstyp ist noch keine sichere automatische Reconciliation verfügbar. Der Record bleibt sichtbar und UNKNOWN_RESULT.</small> : null}
                  </div>
                  {action.reconciliation_supported ? (
                    <Button
                      variant="outline"
                      onClick={() => void reconcile(action.action_id)}
                      disabled={busyActionId === action.action_id}
                      data-testid={`action-reconciliation-check-${index}-button`}
                    >
                      {busyActionId === action.action_id ? "Prüft …" : "Ergebnis prüfen"}
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </AionSectionCard>
    </>
  );
};
